import React, { useState, useEffect, useCallback, useContext, forwardRef, useRef } from 'react';
import OnceFooter from "./../common/Footer/OnceFooter";
import Menu from "./../common/Menu/Menu";
import api from './../../utils/api';
import usePaymentFlow from './../../hooks/use-payment-flow';
import { getPendingPayment, clearPendingPayment } from './../../utils/pendingPayment';
import releasePendingHold from './../../utils/releasePendingHold';
import { saveCartSnapshot, getCartSnapshot, clearCartSnapshot } from './../../utils/cartSnapshot';
import PendingBookingModal from './PendingBookingModal';
import BookingBlockedModal from './BookingBlockedModal';
import { AppContext } from "./../context/ContextProvider";
import { Modal, Button, Row, Col, Form, Card, Collapse } from 'react-bootstrap';
import { numberFormat, inputKeyPressOnlyNumber, optionValue, checkInputIsTaxID, transformFieldWithLang } from './../../utils/functions';
import { toast } from 'react-toastify';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import './PendingBookingModal.css';
import moment from "moment";
import 'moment/locale/th'
import _ from "lodash";
import ReactGA from 'react-ga4';
import { Link } from "react-router-dom";
import Select from "react-select";
import { Lightbox } from "react-modal-image";
import PolicyConfirmModal from './PolicyConfirmModal';
import PaymentTypeChoices from './PaymentTypeChoices';
import { paymentBreakdown, sumBreakdowns } from './../../utils/paymentBreakdown';
import { useNavigate } from "react-router-dom";
import { useTranslation } from 'react-i18next'
import { checkPromoCode, getPendingPromo, clearPendingPromo } from '../../utils/promoCode';

export default function BookingCart() {
  const navigate = useNavigate();
  const { getCurrentHotel, bookingCartValue, getBookingCartData, handleSetBookingCartData, getAgentCodekey } = useContext(AppContext);
  const [bookingCartData, setBookingCartData] = useState(getBookingCartData() || {});
  const updateBookingCart = (data) => {
    handleSetBookingCartData(data);
    setBookingCartData(data);
  };

  const { t, i18n } = useTranslation()
  const lang = i18n.language || 'th'

  const toggleLang = () => {
    const next = lang === 'th' ? 'en' : 'th'
    i18n.changeLanguage(next)
    localStorage.setItem('trans_lang', next)
  }

  const currentHotel = getCurrentHotel();

  // Hotels on the payment gateway collect money through Omise right after the booking is
  // created, so there is no bank transfer and no slip to attach.
  //
  // The hotel in localStorage is a snapshot taken when the customer first opened the hotel
  // page and is never refreshed, so it can say "transfer" for a hotel that has since moved
  // to the gateway. That mismatch is expensive: the customer would be shown the transfer
  // form and actually pay by bank, while the API (which reads the hotel live) creates a
  // gateway booking that discards the slip and gets cancelled by cron minutes later.
  // So the flow is re-read from the server on every visit to this page.
  const { isGatewayFlow } = usePaymentFlow(currentHotel?.id, currentHotel?.payment_flow_type);

  // ตะกร้าถูกล้างตอนสร้างการจอง (ถูกแล้ว -- ไม่งั้นกดจองซ้ำจะกันห้องซ้อน) แต่ถ้าลูกค้า
  // กดย้อนกลับจากหน้าชำระเงินจะเจอตะกร้าว่างโดยไม่รู้ว่าเกิดอะไรขึ้น -- เก็บทางกลับไว้ให้
  const [pendingPayment, setPendingPayment] = useState(() => getPendingPayment());
  const [showPendingDetail, setShowPendingDetail] = useState(false);
  const [showBlockedModal, setShowBlockedModal] = useState(false);
  const [releasing, setReleasing] = useState(false);

  // เอาข้อมูลที่กรอกไว้กลับเข้าตะกร้า -- ใช้เมื่อการจองเดิมจบไปแล้วเท่านั้น
  // ถ้าเอากลับมาตอนการจองยังมีชีวิตอยู่ ลูกค้าจะกดจองซ้ำแล้วกันห้องซ้อนกัน
  const restoreFromSnapshot = useCallback(() => {
    const snap = getCartSnapshot();
    if (!snap?.cart) return false;
    updateBookingCart(snap.cart);
    if (snap.form) setForm(snap.form);
    if (snap.startDate) setStartDate(new Date(snap.startDate));
    if (snap.endDate) setEndDate(new Date(snap.endDate));
    clearCartSnapshot();
    clearPendingPayment();
    setPendingPayment(null);
    return true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // การจองที่ค้างอยู่ยังมีชีวิตไหม -- ถ้าถูกยกเลิกไปแล้ว (จ่ายไม่ทัน) ให้คืนข้อมูลทันที
  // แทนที่จะโชว์ปุ่ม "กลับไปชำระเงิน" ที่กดไปก็เจอหน้าหมดเวลา
  useEffect(() => {
    if (!pendingPayment?.id) return;
    const key = pendingPayment.type === 'cart' ? 'booking_cart_id'
      : (pendingPayment.type === 'main' ? 'booking_main_id' : 'booking_id');
    api.request('omise-payments/hold', (res, success) => {
      if (!success || !res) return;
      if (res.paid) { clearCartSnapshot(); clearPendingPayment(); setPendingPayment(null); return; }
      if (!res.alive) {
        if (restoreFromSnapshot()) toast.info(t('pending_payment_restored'), { autoClose: 10000 });
        else { clearPendingPayment(); setPendingPayment(null); }
      }
    }, { [key]: pendingPayment.id }, 'GET', { showError: false });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingPayment?.id]);

  // ลูกค้ากดยกเลิกเอง -- ปล่อยห้องทันทีแล้วคืนข้อมูล ไม่ต้องรอ job
  const cancelPendingAndRestore = () => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(t('pending_payment_cancel_confirm'))) return;
    setReleasing(true);
    releasePendingHold(pendingPayment, (outcome, statusPath) => {
      setReleasing(false);
      if (outcome === 'failed') {
        // ห้องยังถูกกันอยู่ -- คงรายละเอียดการรอชำระไว้ให้กดใหม่ได้
        toast.error(t('pending_payment_cancel_failed'));
        return;
      }
      setShowPendingDetail(false);
      if (outcome === 'paid') {
        clearCartSnapshot();
        setPendingPayment(null);
        toast.success(t('pending_payment_already_paid'), { autoClose: 10000 });
        if (statusPath) window.location.assign(statusPath);
        return;
      }
      if (!restoreFromSnapshot()) { clearPendingPayment(); setPendingPayment(null); }
    });
  };

  const [startDate, setStartDate] = useState(bookingCartData?.start_date ? new Date(bookingCartData.start_date) : null);
  const [endDate, setEndDate] = useState(bookingCartData?.end_date ? new Date(bookingCartData.end_date) : null);

  const dayQty = startDate && endDate ? moment(endDate).diff(moment(startDate), 'days') : 0;

  const [roomUnavailableIds, setRoomUnavailableIds] = useState([]);

  const [form, setForm] = useState({
    // ไม่เลือกให้ล่วงหน้า -- ลูกค้าต้องเลือกเอง (มีตัวเลือกเดียวจะถูกเลือกให้อัตโนมัติ ดู payChoices)
    payment_type: null,
    payment_state: 'waiting',
    state: 'payment',
    booking_date: new Date(),
    hotel_id: currentHotel?.id,
    booking_note: '',
    slip_file: null
  });
  const [showPolicyModal, setShowPolicyModal] = useState(false);
  const [showConfrimModal, setShowConfrimModal] = useState(false);
  const [acceptPdpa, setAcceptPdpa] = useState(false)
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const slipSectionRef = useRef(null);
  const submitButtonRef = useRef(null);

  const [addons, setAddons] = useState([]);
  const [addonOptions, setAddonsOptions] = useState([]);
  const [freeAddonOptions, setFreeAddonOptions] = useState([]);
  const [foodMenuOptions, setFoodMenuOptions] = useState([]); // มีเมนู = ต้องเลือก 1 เมนูต่อห้อง (API คืน [] ถ้าที่พักปิดส่งอาหาร)
  const [showSuggestionModal, setShowSuggestionModal] = useState(false);
  const [addonQtyWarning, setAddonQtyWarning] = useState(null); // { roomId, ads }

  const allowPayOnStay = !!currentHotel?.allow_payonstay
  const hotelDepositEnabled = currentHotel?.deposit_enabled

  // วิธีชำระที่ที่พักเปิดให้เลือก -- มีทางเดียวเลือกให้เลย มีหลายทางต้องให้ลูกค้าเลือกเอง
  const payChoices = ['full', ...(hotelDepositEnabled ? ['deposit'] : []), ...(allowPayOnStay ? ['payonstay'] : [])]
  const payChoicesKey = payChoices.join(',')
  const payChoicesRef = useRef(null)
  const [payTypeInvalid, setPayTypeInvalid] = useState(false)

  useEffect(() => {
    if (!currentHotel?.id) return
    if (payChoices.length === 1 && form.payment_type !== payChoices[0]) {
      setForm((f) => ({ ...f, payment_type: payChoices[0] }))
    } else if (form.payment_type && !payChoices.includes(form.payment_type)) {
      setForm((f) => ({ ...f, payment_type: null }))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [payChoicesKey, currentHotel?.id])

  const requirePaymentType = () => {
    if (form.payment_type) return true
    setPayTypeInvalid(true)
    toast.warn(t('please_select_payment_type'))
    if (payChoicesRef.current) payChoicesRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' })
    return false
  }
  const hotelDepositType = currentHotel?.deposit_type || 'percent'
  const hotelDepositValue = currentHotel?.deposit_value || 0

  const [petOptions, setPetOptions] = useState([]);

  const discountCalculated = (roomId) => {
    let cartItems = bookingCartData?.cart_items || []
    const itemIndex = cartItems.findIndex(ci => ci.id === roomId)
    if (itemIndex === -1) {
      return 0;
    }

    const cartItem = cartItems[itemIndex];
    const bookingData = cartItem?.booking_items || [];
    const _totalPrice = _.sumBy(bookingData, (o) => Number(o.price || 0))
    const discount = cartItem?.discount || null;
    const couponCode = cartItem?.coupon_code || '';

    let discountPrice = 0

    if (discount && (discount?.coupon || discount?.code)) {
      const c = !!discount?.coupon ? discount?.coupon : discount
      const couponDiscountPrice = Number(c.discount_price) || 0

      if (c.discount_type === 'static') {
        if (!!c.allow_static_discount_per_day) {
          const dayLimit = Number(c.discount_day_limit || 0)

          const _days = dayLimit > 0 && dayLimit < dayQty ? dayLimit : dayQty
          discountPrice = couponDiscountPrice * _days
        } else {
          discountPrice = couponDiscountPrice
        }
      } else {
        discountPrice = (Number(_totalPrice || 0) || 0) * (couponDiscountPrice / 100)
      }

      if (c.discount_type === 'percent') {
        const dayLimit = Number(c.discount_day_limit || 0)
        const discountLimit = Number(c.discount_price_limit || 0)

        if (dayLimit > 0 && dayLimit < dayQty) {
          let discountPerDays = []
          bookingData.map((_b, _i) => {
            if (_i >= dayLimit) {
              return
            }

            discountPerDays.push((Number(_b.price) || 0) * (couponDiscountPrice / 100) || 0)
          })

          discountPrice = _.sum(discountPerDays) || 0
        }

        if (discountLimit > 0 && discountPrice > discountLimit) {
          discountPrice = discountLimit
        }
      }

      if (discountPrice > Number(_totalPrice || 0)) {
        discountPrice = Number(_totalPrice || 0);
      }
    } else if (discount && discount?.voucher) {
      discountPrice = Number(_totalPrice || 0);
    }

    return discountPrice;
  }

  useEffect(() => {
    // ยังไม่ได้เลือก (null) ห้ามล้างคูปอง -- เดิมค่าเริ่มต้นเป็น full จึงไม่เคยเจอกรณีนี้
    if (form?.payment_type && form.payment_type !== 'full' && !currentHotel?.allow_usediscount_with_patialpayment) {
      // remove discount when not allow
      bookingCartData?.cart_items?.map((item) => {
        let cartItems = bookingCartData?.cart_items || []
        const itemIndex = cartItems.findIndex(ci => ci.id === item.id)
        if (itemIndex > -1) {
          cartItems[itemIndex] = {
            ...cartItems[itemIndex],
            discount: null,
            coupon_code: ''
          }
          updateBookingCart({
            ...bookingCartData,
            cart_items: cartItems,
          });
        }
      })
    }

  }, [form?.payment_type])


  const checkRepeatDiscountCode = (roomId, code) => {
    let cartItems = bookingCartData?.cart_items || []
    const duplicate = cartItems.some(ci => ci.id !== roomId && ci.coupon_code === code && !!ci.discount)
    return duplicate
  }

  const applyCouponCode = (item, code, onInvalid, silent = false) => {
    if (checkRepeatDiscountCode(item.id, code)) {
      toast.warn(t('cart_discount_code_warning'))
      return
    }

    setCartItemForm(item.id, null, 'discount') // reset discount first
    checkPromoCode(code, {
      hotel_id: currentHotel?.id,
      start_date: moment(startDate).format('YYYY-MM-DD'),
      end_date: moment(endDate).subtract(1, 'days').format('YYYY-MM-DD'),
      room_id: item?.id || null,
      room_type_id: item?.room_type_id || null,
      day_qty: dayQty,
      from_web: true
    }, (d) => setCartItemForm(item.id, d, 'discount'), onInvalid, { silent })
  }

  // รหัสจากหน้ารายละเอียด voucher/คูปอง (กด "จองเลย")
  // voucher/คูปองบางใบใช้ได้เฉพาะบางห้อง จึงลองกับห้องที่ยังไม่มีส่วนลดไล่ไปทีละห้อง
  // จนกว่าจะผ่าน -- ทุกห้องใช้การตรวจชุดเดียวกับตอนลูกค้ากดเอง
  // ไม่ผ่านสักห้อง: คารหัสไว้ที่ห้องแรกให้ลูกค้าเห็นว่าติดเงื่อนไขอะไร
  useEffect(() => {
    const pending = getPendingPromo(currentHotel?.id)
    const items = bookingCartData?.cart_items || []
    if (!pending || !startDate || !endDate || !items.length) return
    if (items.some((ci) => ci.coupon_code === pending.code && !!ci.discount)) return

    const candidates = items.filter((ci) => !ci.discount)
    const tryAt = (i, lastMessage) => {
      if (i >= candidates.length) {
        setCartItemForm(candidates[0].id, pending.code, 'coupon_code')
        if (lastMessage) toast.warn(lastMessage)
        return
      }

      setCartItemForm(candidates[i].id, pending.code, 'coupon_code')
      applyCouponCode(candidates[i], pending.code, (message) => {
        setCartItemForm(candidates[i].id, '', 'coupon_code')
        tryAt(i + 1, message || lastMessage)
      }, true)
    }

    if (candidates.length) tryAt(0)
  }, [])

  // ห้องที่ถูกถอดส่วนลดตอนเปลี่ยนวัน (getnewRoomsPriceForDateRange) -- ตรวจรหัสเดิมกับวันใหม่
  // ผ่านก็ใส่คืน ไม่ผ่านจะเตือนด้วยข้อความจากเซิร์ฟเวอร์และคารหัสไว้ในช่อง
  const discountRecheckIds = (bookingCartData?.cart_items || []).filter((ci) => ci.discount_recheck).map((ci) => ci.id).join(',')
  useEffect(() => {
    if (!discountRecheckIds || !startDate || !endDate) return
    const items = (bookingCartData?.cart_items || []).filter((ci) => ci.discount_recheck)
    items.forEach((ci) => setCartItemForm(ci.id, false, 'discount_recheck'))
    items.forEach((ci) => {
      if (ci.coupon_code) applyCouponCode(ci, ci.coupon_code)
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [discountRecheckIds])

  const validateGuestQty = (room, guestQty) => {
    const _guestLimit = Number(room?.guest_qty || 0)

    if (_guestLimit > 0 && guestQty > _guestLimit) {
      toast.warn(t('guest_qty_exceed_with_update_qty', { limit: _guestLimit }))
      return false
    }

    return true
  }

  const validateBabyQty = (room, guestQty) => {
    if (!room?.guest_baby_fixed_enabled) {
      return true;
    }

    const _guestLimit = Number(room?.guest_baby_qty || 0)

    if (_guestLimit > 0 && guestQty > _guestLimit) {
      toast.warn(t('guest_baby_qty_exceed_with_update_qty', { limit: _guestLimit }))
      return false
    }

    return true
  }

  const validateAdditionalGuestQty = (room, guestQty) => {
    const _guestLimit = Number(room?.additional_guest_qty || 0)

    if (_guestLimit > 0 && guestQty > _guestLimit) {
      toast.warn(t('guest_additional_exceed', { limit: _guestLimit }))
      return false
    }

    return true
  }

  const validateAdditionalBabyQty = (room, guestQty) => {
    const _guestLimit = Number(room?.additional_baby_qty || 0)

    if (_guestLimit > 0 && guestQty > _guestLimit) {
      toast.warn(t('guest_baby_additional_exceed', { limit: _guestLimit }))
      return false
    }

    return true
  }

  const setCartFoodMenuData = (roomId, foodMenu) => {
    let cartItems = bookingCartData?.cart_items || []
    const itemIndex = cartItems.findIndex(ci => ci.id === roomId)
    if (itemIndex > -1) {
      cartItems[itemIndex] = {
        ...cartItems[itemIndex],
        bookingFoodMenu: foodMenu
      }
      updateBookingCart({
        ...bookingCartData,
        cart_items: cartItems,
      })
    }
  }

  const setCartFreeAddonData = (roomId, freeAddon) => {
    let cartItems = bookingCartData?.cart_items || []
    const itemIndex = cartItems.findIndex(ci => ci.id === roomId)
    if (itemIndex > -1) {
      cartItems[itemIndex] = {
        ...cartItems[itemIndex],
        bookingFreeAddon: freeAddon
      }
      updateBookingCart({
        ...bookingCartData,
        cart_items: cartItems,
      });
    }
  }

  const setCartAddonsData = (roomId, addonsPerRoom) => {
    let cartItems = bookingCartData?.cart_items || []
    const itemIndex = cartItems.findIndex(ci => ci.id === roomId)
    if (itemIndex > -1) {
      cartItems[itemIndex] = {
        ...cartItems[itemIndex],
        bookingAddons: addonsPerRoom
      }
      updateBookingCart({
        ...bookingCartData,
        cart_items: cartItems,
      });
    }
  }

  const setAdditionalPetsData = (roomId, petsPerRoom) => {
    let cartItems = bookingCartData?.cart_items || []
    const itemIndex = cartItems.findIndex(ci => ci.id === roomId)
    if (itemIndex > -1) {
      cartItems[itemIndex] = {
        ...cartItems[itemIndex],
        bookingPets: petsPerRoom
      }
      updateBookingCart({
        ...bookingCartData,
        cart_items: cartItems,
      });
    }
  }

  const setCartItemForm = (roomId, guestQty, _field = 'form_guest_qty') => {
    let cartItems = bookingCartData?.cart_items || []
    const itemIndex = cartItems.findIndex(ci => ci.id === roomId)
    if (itemIndex > -1) {
      cartItems[itemIndex] = {
        ...cartItems[itemIndex],
        [_field]: guestQty
      }
      updateBookingCart({
        ...bookingCartData,
        cart_items: cartItems,
      });
    }
  }

  const setCartItemCollapse = (roomId, hideData = false) => {
    let cartItems = bookingCartData?.cart_items || []
    const itemIndex = cartItems.findIndex(ci => ci.id === roomId)
    if (itemIndex > -1) {
      cartItems[itemIndex] = {
        ...cartItems[itemIndex],
        hideCollapse: hideData,
      }
      updateBookingCart({
        ...bookingCartData,
        cart_items: cartItems,
      });
    }
  }

  const ExampleCustomInput = forwardRef(({ value, onClick }, ref) => (
    <button className="px-3 py-1 custom-dateinput-search" onClick={onClick} ref={ref}>
      <div className="d-flex align-items-center">
        <div className="checktime-info ps-2">
          <p className="mb-0">{t('check_in')}</p>
          <div className="display-date">{startDate ? moment(startDate).format('DD/MM/YYYY') : ''} </div>
        </div>
        <div className="mx-3" style={{ height: 40, width: 1, backgroundColor: '#000000' }}></div>
        <div className="checktime-info">
          <p className="mb-0">{t('check_out')}</p>
          <div className="display-date">{endDate ? moment(endDate).format('DD/MM/YYYY') : ''} </div>
        </div>
      </div>
    </button>
  ));

  const checkBookingAndBlocked = (start_date, end_date) => {
    const items = bookingCartData?.cart_items || [];

    const beforeBookingStartDateIds = items
      .filter(item => !!item.booking_start_date && moment(start_date).isBefore(moment(item.booking_start_date), 'day'))
      .map(item => item.id);

    api.request(`bookings/all-check-rooms-available`, (data) => {
      const unavailableRoomIds = data?.unavailable_room_ids || [];
      setRoomUnavailableIds(_.uniq([...unavailableRoomIds, ...beforeBookingStartDateIds]));
    }, {
      hotel_id: currentHotel?.id,
      room_ids: items.map(item => item.id),
      start_date: moment(start_date).format('YYYY-MM-DD'),
      end_date: moment(end_date).subtract(1, 'days').format('YYYY-MM-DD'),
    })
  }

  const getnewRoomsPriceForDateRange = (start_date, end_date, petIds = []) => {
    const items = bookingCartData?.cart_items || [];
    api.request(`rooms/calulatemultipleprice`, (data) => {
      const prices = data || [];
      let newCartItems = items.map((item) => {
        const roomPrices = prices.find(rp => rp.room_id === item.id);
        if (roomPrices) {
          const booking_items = roomPrices.prices.map((p) => ({
            date: p.date,
            price: p.price,
            additional_guest_price: p.additional_guest_price || 0,
            additional_baby_price: p.additional_baby_price || 0,
            room_id: item.id,
          }));
          // ส่วนลดตรวจผ่านกับวันเดิม -- เปลี่ยนวันแล้วต้องตรวจใหม่ (จำนวนคืน ช่วงวันเข้าพัก)
          // ถอดออกก่อนเพื่อไม่ให้คิดราคาผิด (เช่น voucher 1 คืนลดให้ทั้ง 5 คืน) แล้วค่อยใส่คืนเมื่อผ่าน
          return {
            ...item,
            booking_items: booking_items,
            ...(item.discount ? { discount: null, discount_recheck: true } : {}),
          };
        } else {
          return item;
        }
      });

      if (petIds && petIds.length > 0) {
        api.request(`hotel-additional-pets/findwithprice`, (data) => {
          if (data && data.length > 0) {
            newCartItems = newCartItems.map((item) => {
              const hasPets = item?.bookingPets && item?.bookingPets.length > 0;
              if (!hasPets) {
                return item;
              } else {
                const bookingPet = item?.bookingPets[0];
                const _petPriceData = data.find(p => p.id === bookingPet?.pet_id);

                if (!_petPriceData) {
                  return { ...item };
                }

                let petPricePerQtyUnit = _.sumBy(_petPriceData.days_price || [], (dp) => { return Number(dp.price || 0) }) || 0;

                return {
                  ...item,
                  bookingPets: [{
                    ...bookingPet,
                    unit_price: petPricePerQtyUnit,
                    total_price: petPricePerQtyUnit * (bookingPet?.qty || 1),
                    _prices: _petPriceData.days_price || [],
                  }]
                }
              }
            });

            updateBookingCart({
              ...bookingCartData,
              start_date: start_date,
              end_date: end_date,
              cart_items: newCartItems,
            });
          } else {
            updateBookingCart({
              ...bookingCartData,
              start_date: start_date,
              end_date: end_date,
              cart_items: newCartItems,
            });
          }
        }, {
          ids: petIds,
          hotel_id: currentHotel?.id,
          enabled: true,
          start_date: moment(start_date).format('YYYY-MM-DD'),
          end_date: moment(end_date).subtract(1, 'days').format('YYYY-MM-DD'),
          allow_customer_booking: true,
        })
      } else {
        updateBookingCart({
          ...bookingCartData,
          start_date: start_date,
          end_date: end_date,
          cart_items: newCartItems,
        });
      }
    }, {
      hotel_id: currentHotel?.id,
      room_ids: items.map(item => item.id),
      start_date: moment(start_date).format('YYYY-MM-DD'),
      end_date: moment(end_date).format('YYYY-MM-DD'),
    })
  }

  useEffect(() => {
    if (startDate && endDate) {
      checkBookingAndBlocked(startDate, endDate);

      const petsIdsInCart = bookingCartData?.cart_items?.map((item) => {
        const allowPets = currentHotel?.addition_pets_type === 'all-rooms' || (currentHotel?.addition_pets_type === 'specific-rooms' && item?.allow_pet);
        if (allowPets && item?.bookingPets && item?.bookingPets.length > 0) {
          return item?.bookingPets?.map((p) => p.pet_id) || [];
        } else {
          return [];
        }
      }).flat().filter(id => !!id) || [];

      getnewRoomsPriceForDateRange(startDate, endDate, _.uniq(petsIdsInCart));
      if (['all-rooms', 'specific-rooms'].includes(currentHotel?.addition_pets_type)) {
        fetchPetOptions(startDate, endDate);
      }
    }
  }, [startDate, endDate]);

  const fetchHotelAddonsItem = () => {
    api.request(`hotel-addons/all`, (data) => {
      setAddonsOptions(data.map((item) => {
        return {
          label: transformFieldWithLang(item, 'name', lang),
          value: item.id,
          _price: item.default_price || 0
        }
      }))
    }, { hotel_id: currentHotel?.id, enabled: true, is_non_inventory: true })
  }

  const fetchHotelFreeAddonsItem = () => {
    api.request(`hotel-free-addons/all`, (data) => {
      setFreeAddonOptions(data.map((item) => ({
        label: transformFieldWithLang(item, 'name', lang),
        value: item.id,
      })))
    }, { hotel_id: currentHotel?.id, enabled: true, is_non_inventory: true })
  }

  const fetchHotelFoodMenus = () => {
    api.request(`hotel-food-menus`, (data) => {
      setFoodMenuOptions((Array.isArray(data) ? data : []).map((item) => ({
        label: transformFieldWithLang(item, 'name', lang),
        value: item.id,
      })))
    }, { hotel_id: currentHotel?.id })
  }

  const fetchPetOptions = (start_date, end_date) => {
    api.request(`hotel-additional-pets/findwithprice`, (data) => {
      setPetOptions(data.map((item) => {
        const _qtyLimit = item.qty_limit || 0;
        const _baseName = transformFieldWithLang(item, 'name', lang);
        const _label = _qtyLimit > 0 ? `${_baseName} (${t('pet_qty_limit_label', { limit: _qtyLimit })})` : _baseName;
        return {
          label: _label,
          value: item.id,
          _prices: item.days_price || [],
          qty_limit: _qtyLimit,
        }
      }))
    }, {
      hotel_id: currentHotel?.id,
      enabled: true,
      start_date: moment(start_date).format('YYYY-MM-DD'),
      end_date: moment(end_date).subtract(1, 'days').format('YYYY-MM-DD'),
      allow_customer_booking: true,
    })
  }

  useEffect(() => {
    if (currentHotel?.id) {
      fetchHotelAddonsItem(currentHotel.id);
      fetchHotelFreeAddonsItem();
      fetchHotelFoodMenus();
    }
  }, []);

  const validateAddons = () => {
    const items = bookingCartData?.cart_items || [];
    const addonsAllRooms = items.map((item) => item.bookingAddons || []).flat();

    if (!addonsAllRooms.length) {
      return true
    }

    let l = (addonsAllRooms.filter((x) => !!x.addons_id)) || []

    return l.length === addonsAllRooms.length
  }

  const validateFreeAddons = () => {
    const items = bookingCartData?.cart_items || [];
    for (const item of items) {
      if (freeAddonOptions.length > 0 && !item.bookingFreeAddon?.free_addon_id) {
        return false;
      }
    }
    return true;
  }

  // เมนูที่เลือกไว้ต้องยังอยู่ในรายการ (ตะกร้าเก็บค้างไว้ข้ามวัน แอดมินอาจปิดเมนูนั้นไปแล้ว)
  const validateFoodMenus = () => {
    if (!foodMenuOptions.length) return true;
    return (bookingCartData?.cart_items || []).every((item) => foodMenuOptions.some((o) => o.value === item.bookingFoodMenu?.food_menu_id));
  }

  // ยังไม่เลือกวิธีชำระ = ยังไม่มียอด (ห้ามตกไปคิดแบบมัดจำเหมือนสูตรเดิมเมื่อ payment_type ไม่ใช่ full)
  const getPayAmount = () => {
    if (!form.payment_type) return 0
    return _.sumBy(bookingCartData?.cart_items || [], (item) => {
      const priceTotal = _.sumBy(item?.booking_items, 'price');
      const discountPrice = discountCalculated(item.id);
      const addonsPrices = _.sumBy(item?.bookingAddons || [], (x) => { return Number(x.total_price || 0) }) || 0;
      const additionalGuestTotalPrice = summaryGuestAdditionalPrice(item) + summaryBabyAdditionalPrice(item);
      const additionalPetPrice = _.sumBy(item?.bookingPets || [], (x) => { return Number(x.total_price || 0) }) || 0;
      const sumAddons = Number(addonsPrices || 0) + Number(additionalGuestTotalPrice || 0) + Number(additionalPetPrice || 0);

      // >> Deposit Calculation
      const depositConfigType = item?.deposit_enabled ? 'room' : (currentHotel?.deposit_enabled ? 'hotel' : null)
      const depositEnabled = !!depositConfigType
      const depositeType = depositConfigType === 'room' ? item?.deposit_type : currentHotel?.deposit_type
      const depositValue = depositConfigType === 'room' ? (item?.deposit_value || 0) : (currentHotel?.deposit_value || 0)
      const depositPrice = depositeType === 'static' ? depositValue : ((Number(priceTotal || 0) - (!!currentHotel?.allow_usediscount_with_patialpayment ? discountPrice : 0)) * (depositValue / 100)) || 0
      const addonPriceForPay = (form.payment_type !== 'deposit' || !currentHotel?.deposit_addon_enabled) ? sumAddons : (depositeType === 'static' ? 0 : (Number(sumAddons) * (depositValue / 100)))
      const roomDepositEnabled = item?.room_deposit_enabled || currentHotel?.room_deposit_enabled || false
      const roomDepositPrice = roomDepositEnabled ? (item?.room_deposit_price || currentHotel?.room_deposit_price || 0) : 0
      const roomDepositPayOnCheckin = currentHotel?.room_deposit_payoncheckin || false
      const roomDepositPayAmount = roomDepositPayOnCheckin ? 0 : roomDepositPrice
      // << Deposit Calculation
      const totalAmount = priceTotal - discountPrice + sumAddons
      return (form.payment_type === 'full' ? (Number(priceTotal || 0) - discountPrice + sumAddons) : (form.payment_type === 'payonstay' ? (sumAddons || 0) : (Number(depositPrice || 0) + Number(addonPriceForPay)))) + Number(roomDepositPayAmount || 0)
    }
    ) || 0;
  }

  // ยอดของแต่ละวิธีชำระ สำหรับการ์ดเลือกวิธีชำระ -- สูตรเดียวกับ getPayAmount
  const cartBreakdown = (type) => sumBreakdowns((bookingCartData?.cart_items || []).map((item) => {
    const priceTotal = _.sumBy(item?.booking_items, 'price');
    const discountPrice = discountCalculated(item.id);
    const addonsPrices = _.sumBy(item?.bookingAddons || [], (x) => Number(x.total_price || 0)) || 0;
    const additionalGuestTotalPrice = summaryGuestAdditionalPrice(item) + summaryBabyAdditionalPrice(item);
    const additionalPetPrice = _.sumBy(item?.bookingPets || [], (x) => Number(x.total_price || 0)) || 0;
    const sumAddons = Number(addonsPrices || 0) + Number(additionalGuestTotalPrice || 0) + Number(additionalPetPrice || 0);
    const depositConfigType = item?.deposit_enabled ? 'room' : (currentHotel?.deposit_enabled ? 'hotel' : null)
    const depositeType = depositConfigType === 'room' ? item?.deposit_type : currentHotel?.deposit_type
    const depositValue = depositConfigType === 'room' ? (item?.deposit_value || 0) : (currentHotel?.deposit_value || 0)
    const depositPrice = depositeType === 'static' ? depositValue : ((Number(priceTotal || 0) - (!!currentHotel?.allow_usediscount_with_patialpayment ? discountPrice : 0)) * (depositValue / 100)) || 0
    const roomDepositEnabled = item?.room_deposit_enabled || currentHotel?.room_deposit_enabled || false
    return paymentBreakdown({
      type,
      roomTotal: priceTotal,
      discount: discountPrice,
      sumAddons,
      depositPrice,
      depositAddonEnabled: !!currentHotel?.deposit_addon_enabled,
      depositType: depositeType,
      depositValue,
      allowDiscountPartial: !!currentHotel?.allow_usediscount_with_patialpayment,
      securityPrice: roomDepositEnabled ? (item?.room_deposit_price || currentHotel?.room_deposit_price || 0) : 0,
      securityOnCheckin: !!currentHotel?.room_deposit_payoncheckin,
    });
  }));

  const getDepositAmount = () => {
    return _.sumBy(bookingCartData?.cart_items || [], (item) => {
      const priceTotal = _.sumBy(item?.booking_items, 'price');
      const discountPrice = discountCalculated(item.id);
      // >> Deposit Calculation
      const depositConfigType = item?.deposit_enabled ? 'room' : (currentHotel?.deposit_enabled ? 'hotel' : null)
      const depositEnabled = !!depositConfigType
      const depositeType = depositConfigType === 'room' ? item?.deposit_type : currentHotel?.deposit_type
      const depositValue = depositConfigType === 'room' ? (item?.deposit_value || 0) : (currentHotel?.deposit_value || 0)
      const depositPrice = depositeType === 'static' ? depositValue : ((Number(priceTotal || 0) - (!!currentHotel?.allow_usediscount_with_patialpayment ? discountPrice : 0)) * (depositValue / 100)) || 0
      const addonsPrice = _.sumBy(item?.bookingAddons || [], (x) => { return Number(x.total_price || 0) }) || 0;
      const additionalGuestTotalPrice = summaryGuestAdditionalPrice(item) + summaryBabyAdditionalPrice(item);
      const additionalPetPrice = _.sumBy(item?.bookingPets || [], (x) => { return Number(x.total_price || 0) }) || 0;
      const sumAddons = Number(addonsPrice || 0) + Number(additionalGuestTotalPrice || 0) + Number(additionalPetPrice || 0);
      const addonPriceForPay = ((!currentHotel?.deposit_addon_enabled) ? sumAddons : (depositeType === 'static' ? 0 : (Number(sumAddons) * (depositValue / 100)))) || 0
      // << Deposit Calculation
      return Number(depositPrice) + Number(addonPriceForPay);
    }
    ) || 0;
  }

  const sumTotalPrice = () => {
    return _.sumBy(bookingCartData?.cart_items || [], (item) => {
      return _.sumBy(item?.booking_items, 'price');
    }) || 0;
  }

  // dryRun: ตรวจข้อมูลอย่างเดียว ไม่ส่งการจอง (ที่พัก gateway ตรวจก่อนเปิดเงื่อนไข)
  const handleSubmitBooking = (skipAddonQtyCheck = false, { dryRun = false } = {}) => {
    if (submitting) {
      return false
    }

    // ด่านสุดท้าย -- ปกติกดเพิ่มห้องลงตะกร้าไม่ได้อยู่แล้วถ้ายังมีรายการค้าง
    // แต่ตะกร้าเก่าที่ค้างอยู่ในเครื่องก็พามาถึงตรงนี้ได้ ปล่อยผ่านคือกันห้องซ้อนสองใบ
    if (pendingPayment?.id) {
      setShowBlockedModal(true)
      return
    }

    const items = bookingCartData?.cart_items || [];

    if (!requirePaymentType()) return false


    if (items?.length === 0) {
      toast.warn(t('please_select_room_min'))
      return
    }

    if (dayQty < 1) {
      toast.warn(t('please_specify_stay_date'))
      return
    }

    if (!form.contact_name) {
      toast.warn(t('please_specify_contact_name'))
      return
    }

    if (!form.contact_tel_1) {
      toast.warn(t('please_specify_contact_tel'))
      return
    }

    if (!validateAddons()) {
      toast.warn(t('please_check_addons'))
      return
    }

    if (!validateFreeAddons()) {
      toast.warn(t('please_select_free_addon'))
      return
    }

    if (!validateFoodMenus()) {
      toast.warn(t('please_select_food_menu'))
      return
    }

    if (!dryRun && !skipAddonQtyCheck && dayQty > 1 && items.some(item => (item.bookingAddons || []).some(ad => ad.addons_id && Number(ad.qty) < dayQty))) {
      setAddonQtyWarning({ onConfirm: () => handleSubmitBooking(true) });
      return;
    }

    const payAmount = getPayAmount();
    const totalPrice = sumTotalPrice();

    if (!totalPrice || totalPrice < 1) {
      toast.warn(t('information_invalid_please_check'))
      return
    }

    if (!isGatewayFlow && payAmount > 0 && !form.slip_file) {
      toast.warn(t('please_attach_payment_slip'))
      return
    }

    if (items.some(item => roomUnavailableIds.includes(item.id))) {
      toast.warn(t('room_unavailable_in_cart'))
      return
    }

    if (items.some(item => Number(item?.form_guest_qty || 0) < 1)) {
      toast.warn(t('please_specify_guest_qty_correctly'))
      return
    }

    if (!!currentHotel && currentHotel.request_customer_tax_info_enabled && !form.customer_tax_id) {
      toast.warn(t('please_specify_customer_tax_id'))
      return
    }

    if (dryRun) return true

    const agent = getAgentCodekey()
    setSubmitting(true)

    const rawForm = {
      ...form,
      day_qty: dayQty,

      start_date: moment(startDate).format('YYYY-MM-DD'),
      end_date: moment(endDate).subtract(1, 'days').format('YYYY-MM-DD'),
      sale_agent_id: (agent && agent?.id) ? agent.id : null,
    }

    const formData = new FormData();
    for (var key in rawForm) {
      if (key !== 'slip_file') {
        formData.append(key, rawForm[key]);
      }
    }
    formData.append("file", rawForm.slip_file);

    // สรุปยอดต่อห้องเก็บไว้ตั้งแต่ตอนนี้ -- ตัวเลขทั้งหมดถูกคำนวณในลูปนี้อยู่แล้ว
    // และสูตรพวกนี้อ่านจาก bookingCartData ที่ยังมีของอยู่ พอสร้างการจองเสร็จตะกร้าจะถูกล้าง
    // คำนวณใหม่ทีหลังจะได้ 0 ทุกช่อง (เคยพลาดมาแล้วในกล่องสรุปการจอง)
    const summaryRooms = [];

    items.map((item, i) => {
      const priceTotal = _.sumBy(item?.booking_items, 'price');
      const addonsPrices = _.sumBy(item?.bookingAddons || [], (x) => { return Number(x.total_price || 0) }) || 0;
      const additionalGuestPrice = summaryGuestAdditionalPrice(item);
      const additionalBabyPrice = summaryBabyAdditionalPrice(item);
      const additionalGuestTotalPrice = Number(additionalGuestPrice || 0) + Number(additionalBabyPrice || 0);
      const additionalPetPrice = _.sumBy(item?.bookingPets || [], (x) => { return Number(x.total_price || 0) }) || 0;
      const sumAddons = Number(addonsPrices || 0) + Number(additionalGuestTotalPrice || 0) + Number(additionalPetPrice || 0);

      const discountPrice = discountCalculated(item.id);
      // >> Deposit Calculation
      const depositConfigType = item?.deposit_enabled ? 'room' : (currentHotel?.deposit_enabled ? 'hotel' : null)

      const depositEnabled = !!depositConfigType
      const depositeType = depositConfigType === 'room' ? item?.deposit_type : currentHotel?.deposit_type
      const depositValue = depositConfigType === 'room' ? (item?.deposit_value || 0) : (currentHotel?.deposit_value || 0)

      const depositPrice = depositeType === 'static' ? depositValue : ((Number(priceTotal || 0) - (!!currentHotel?.allow_usediscount_with_patialpayment ? discountPrice : 0)) * (depositValue / 100)) || 0

      const addonPriceForPay = (form.payment_type !== 'deposit' || !currentHotel?.deposit_addon_enabled) ? sumAddons : (depositeType === 'static' ? 0 : (Number(sumAddons) * (depositValue / 100)))

      const allowPayOnStay = !!currentHotel?.allow_payonstay

      const roomDepositEnabled = item?.room_deposit_enabled || currentHotel?.room_deposit_enabled || false
      const roomDepositPrice = roomDepositEnabled ? (item?.room_deposit_price || currentHotel?.room_deposit_price || 0) : 0
      const roomDepositPayOnCheckin = currentHotel?.room_deposit_payoncheckin || false
      const roomDepositPayAmount = roomDepositPayOnCheckin ? 0 : roomDepositPrice
      // << Deposit Calculation

      const b = {
        room_id: item.id,
        room_name: item?.name_th || '',
        guest_qty: item?.form_guest_qty || 0,
        guest_child_qty: 0,
        booking_note: item?.form_booking_note || '',
        guest_baby_qty: item?.form_guest_baby_qty || 0,
        additional_guest_qty: item?.form_additional_guest_qty || 0,
        additional_baby_qty: item?.form_additional_baby_qty || 0,
        additional_guest_qty_price: additionalGuestPrice,
        additional_baby_qty_price: additionalBabyPrice,
        additional_guest_total_price: additionalGuestTotalPrice,
        tent_qty: 0,
        total_price: priceTotal,
        discount_code: item?.coupon_code || '',
        total_discount: discountPrice,
        booking_items: item?.booking_items?.map((b) => {
          return {
            date: b.date,
            price: b.price,
          }
        }) || [],
        booking_addons: item?.bookingAddons || [],
        free_addon_id: item?.bookingFreeAddon?.free_addon_id || null,
        food_menu_id: foodMenuOptions.length ? (item?.bookingFoodMenu?.food_menu_id || null) : null,
        booking_additional_pets: item?.bookingPets || [],
        deposit_amount: form.payment_type === 'deposit' ? depositPrice : 0,
        deposit_addon_type: (form.payment_type === 'deposit' && !!currentHotel?.deposit_addon_enabled) ? 'deposit' : 'full',
        deposit_addon_amount: (form.payment_type === 'deposit' && !!currentHotel?.deposit_addon_enabled) ? Number(addonPriceForPay) : 0,
        room_deposit_amount: roomDepositPayAmount || 0,
        room_deposit_outstanding: Number(roomDepositPrice) > 0 && roomDepositPayOnCheckin ? roomDepositPrice : 0,
        room_type_id: item?.room_type_id || null,
      }

      formData.append(`room_items[${i}]`, JSON.stringify(b));

      summaryRooms.push({
        name_th: item?.name_th || '',
        name_en: item?.name_en || '',
        guest_qty: Number(item?.form_guest_qty || 0),
        guest_baby_qty: Number(item?.form_guest_baby_qty || 0),
        additional_guest_qty: Number(item?.form_additional_guest_qty || 0),
        additional_baby_qty: Number(item?.form_additional_baby_qty || 0),
        additional_guest_price: Number(additionalGuestPrice || 0),
        additional_baby_price: Number(additionalBabyPrice || 0),
        room_price: Number(priceTotal || 0),
        discount: Number(discountPrice || 0),
        // ชื่อของเสริมอยู่ในตัวเลือกที่โหลดมาจาก API ไม่ได้อยู่ในตะกร้า -- แปลงเป็นชื่อไว้เลย
        addons: (item?.bookingAddons || [])
          .filter((a) => Number(a?.total_price || 0) > 0)
          .map((a) => ({
            label: optionValue(a.addons_id, addonOptions)?.label || t('addons'),
            qty: Number(a.qty || 0),
            total_price: Number(a.total_price || 0),
          })),
        pets: (item?.bookingPets || [])
          .filter((x) => Number(x?.total_price || 0) > 0)
          .map((x) => ({
            label: optionValue(x.pet_id, petOptions)?.label || t('pet'),
            qty: Number(x.qty || 0),
            total_price: Number(x.total_price || 0),
          })),
        security_deposit: Number(roomDepositPrice || 0),
        security_deposit_pay_now: Number(roomDepositPayAmount || 0),
        total: Number(priceTotal || 0) - Number(discountPrice || 0) + Number(sumAddons || 0),
      });
    });

    api.request(`bookings/bookingfromcart`, (res, success) => {
      setSubmitting(false)

      if (!success || !res) {
        return
      } else {
        toast.success(isGatewayFlow && payAmount > 0 ? t('booking_held_pay_now') : t('booking_successful'))
        // เก็บสำเนาไว้ก่อนล้าง -- ถ้าจ่ายไม่ทันหรือเปลี่ยนใจจะได้เอากลับมาให้
        // ไม่ต้องกรอกใหม่ทั้งหมด (ดู utils/cartSnapshot.js)
        if (isGatewayFlow && payAmount > 0) {
          saveCartSnapshot({
            cart: getBookingCartData(),
            form,
            startDate,
            endDate,
            summary: {
              rooms: summaryRooms,
              payment_type: form.payment_type,
              total: _.sumBy(summaryRooms, 'total'),
              security_deposit: _.sumBy(summaryRooms, 'security_deposit'),
              pay_now: payAmount,
            },
          });
        }
        const usedCodes = (bookingCartData?.cart_items || []).filter((ci) => !!ci.discount && !!ci.coupon_code)
        usedCodes.forEach((ci) => clearPendingPromo(ci.coupon_code))
        updateBookingCart({});
        setStartDate(null);
        setEndDate(null);

        if (!res.is_multi) {
          const resBooking = res.booking
          if (isGatewayFlow && payAmount > 0) {
            // nothing is paid yet — collect it through Omise before showing the status page
            navigate(`/once/payment/booking/${resBooking.id}`, {
              state: {
                amount: payAmount,
                booking_no: resBooking.booking_no,
                hotel_id: currentHotel?.id,
                is_deposit: form.payment_type === 'deposit',
              }
            })
            return
          }
          navigate(`/once/status/${resBooking.id}/${resBooking.booking_no}`)
        } else {
          const resCart = res.cart
          if (isGatewayFlow && payAmount > 0) {
            navigate(`/once/payment/cart/${resCart.id}`, {
              state: {
                amount: payAmount,
                transaction_no: resCart.transaction_no,
                hotel_id: currentHotel?.id,
                is_deposit: form.payment_type === 'deposit',
              }
            })
            return
          }
          navigate(`/once/cart-status/${resCart.id}/${resCart.transaction_no}`)
        }
      }
    }, formData, 'POST')
  }

  const summaryGuestAdditionalPrice = (room) => {
    if (!room?.additional_guest_enabled) {
      return 0;
    }
    const _additionalWithRoomInCart = bookingCartData?.cart_items?.find(ci => ci.id === room.id);
    const _additionalGuestQty = Number(_additionalWithRoomInCart?.form_additional_guest_qty || 0);

    let _priceAdditionalGuest = 0;
    room?.booking_items?.forEach((b) => {
      const _pricePerUnit = Number(b.additional_guest_price || 0);
      _priceAdditionalGuest += _pricePerUnit * _additionalGuestQty;
    })
    return _priceAdditionalGuest;
  }

  const summaryBabyAdditionalPrice = (room) => {
    if (!room?.additional_guest_enabled) {
      return 0;
    }
    const _additionalWithRoomInCart = bookingCartData?.cart_items?.find(ci => ci.id === room.id);
    const _additionalGuestQty = Number(_additionalWithRoomInCart?.form_additional_baby_qty || 0);

    let _priceAdditionalGuest = 0;
    room?.booking_items?.forEach((b) => {
      const _pricePerUnit = Number(b.additional_baby_price || 0);
      _priceAdditionalGuest += _pricePerUnit * _additionalGuestQty;
    })
    return _priceAdditionalGuest;
  }

  const checkCartItemsUnavailable = () => {
    const items = bookingCartData?.cart_items || [];
    return items.some(item => roomUnavailableIds.includes(item.id))
  }

  const addonCustomTitle = transformFieldWithLang(currentHotel, 'addons_custom_title', lang)
  const addonCustomWarning = transformFieldWithLang(currentHotel, 'addons_custom_warning', lang)
  const paymentInfo = transformFieldWithLang(currentHotel, 'payment_info', lang)

  return (
    <>
      <Menu header_style_2={true} hide_menu={true} />
      <div className="text-center">
        <h4>{t('booking_detail')}</h4>
        <div className="px-0 px-md-3 px-xl-5">
          <div className="d-flex align-items-center px-5">
            {!!currentHotel?.logo_file_path && <img src={currentHotel?.logo_file_path} className="hotel-logo" alt="img" />}
            <h5 className="mx-2 text-start">{transformFieldWithLang(currentHotel, 'name', lang)}</h5>
          </div>
          <div className="text-start pb-3 px-2 px-xl-5">
            <div className="d-flex flex-column flex-md-row align-items-start align-items-md-center mb-3">
              <DatePicker
                selected={bookingCartData?.start_date ? new Date(bookingCartData.start_date) : null}
                onChange={(dates) => {
                  const [start, end] = dates;
                  setStartDate(start);
                  setEndDate(end);
                }}
                startDate={startDate}
                endDate={endDate}
                selectsRange
                dateFormat="dd/MM/yyyy"
                minDate={new Date()}
                isClearable={false}
                customInput={<ExampleCustomInput />}
              />
              {dayQty > 0 && <p className="mt-2 mb-0 text-black ms-2">{t('qty')} : {dayQty} {t('nights')}</p>}
            </div>
            <Row>
              <Col xs={12} md={6}>
                {/* section for cart items */}
                {(!bookingCartData?.cart_items || bookingCartData?.cart_items?.length === 0) && !!pendingPayment &&
                  <div className="border rounded p-3 my-4">
                    <p className="text-black mb-1"><strong>{t('pending_payment_title')}</strong></p>
                    {!!pendingPayment.booking_no && <p className="mb-1">{t('booking_no')} : {pendingPayment.booking_no}</p>}
                    <p className="text-muted mb-3">{t('pending_payment_hint')}</p>
                    <button
                      type="button"
                      className="btn btn-dark"
                      onClick={() => navigate(`/once/payment/${pendingPayment.type}/${pendingPayment.id}`, {
                        state: {
                          amount: pendingPayment.amount,
                          booking_no: pendingPayment.booking_no,
                          hotel_id: currentHotel?.id,
                        }
                      })}
                    >
                      {t('pending_payment_resume')}
                    </button>
                    <button
                      type="button"
                      className="btn btn-outline-dark ms-2"
                      onClick={() => setShowPendingDetail(true)}
                    >
                      {t('pending_payment_view')}
                    </button>
                    <button
                      type="button"
                      className="btn btn-link text-muted"
                      disabled={releasing}
                      onClick={cancelPendingAndRestore}
                    >
                      {t('pending_payment_cancel')}
                    </button>
                  </div>
                }
                {(!bookingCartData?.cart_items || bookingCartData?.cart_items?.length === 0) && !pendingPayment &&
                  <div className="text-center py-5 d-flex align-items-center">
                    <p className="text-black mb-0">-- {t('cart_empty')} --</p>
                    <Link className="py-2" to={`/${currentHotel?.code}`}>
                      <span className="ms-2 text-danger border border-danger rounded p-2"><i className="fal fa-hand-pointer" style={{ fontSize: 18 }}></i> {t('select_room')}</span>
                    </Link>
                  </div>
                }
                {bookingCartData?.cart_items?.length > 0 && <div className="cart-items">
                  <div className="mb-3">
                    {bookingCartData?.cart_items?.map((item, index) => {
                      const bookingDatesStr = item?.booking_items?.map((b) => `${moment(b.date).format('DD/MM/YYYY')} (${numberFormat(b.price, true)})`).join(', ')
                      const priceTotal = _.sumBy(item?.booking_items, 'price');
                      const isUnavailable = roomUnavailableIds.includes(item.id);

                      const discountPrice = discountCalculated(item.id);
                      const addonsPrice = _.sumBy(item?.bookingAddons || [], (x) => { return Number(x.total_price || 0) }) || 0;
                      const additionalGuestTotalPrice = summaryGuestAdditionalPrice(item) + summaryBabyAdditionalPrice(item);
                      const additionalPetPrice = _.sumBy(item?.bookingPets || [], (x) => { return Number(x.total_price || 0) }) || 0;
                      const sumAddons = Number(addonsPrice || 0) + Number(additionalGuestTotalPrice || 0) + Number(additionalPetPrice || 0);

                      // >> Deposit Calculation
                      const depositConfigType = item?.deposit_enabled ? 'room' : (currentHotel?.deposit_enabled ? 'hotel' : null)

                      const depositEnabled = !!depositConfigType
                      const depositeType = depositConfigType === 'room' ? item?.deposit_type : currentHotel?.deposit_type
                      const depositValue = depositConfigType === 'room' ? (item?.deposit_value || 0) : (currentHotel?.deposit_value || 0)

                      const depositPrice = depositeType === 'static' ? depositValue : ((Number(priceTotal || 0) - (!!currentHotel?.allow_usediscount_with_patialpayment ? discountPrice : 0)) * (depositValue / 100)) || 0

                      const addonPriceForPay = (form.payment_type !== 'deposit' || !currentHotel?.deposit_addon_enabled) ? addonsPrice : (depositeType === 'static' ? 0 : (Number(addonsPrice) * (depositValue / 100)))

                      const allowPayOnStay = !!currentHotel?.allow_payonstay

                      const roomDepositEnabled = item?.room_deposit_enabled || currentHotel?.room_deposit_enabled || false
                      const roomDepositPrice = roomDepositEnabled ? (item?.room_deposit_price || currentHotel?.room_deposit_price || 0) : 0
                      const roomDepositPayOnCheckin = currentHotel?.room_deposit_payoncheckin || false
                      const roomDepositPayAmount = roomDepositPayOnCheckin ? 0 : roomDepositPrice
                      // << Deposit Calculation

                      // >> discount
                      const discount = item?.discount || null;
                      const couponCode = item?.coupon_code || '';
                      // << discount

                      const roomAdditionalPrice = summaryGuestAdditionalPrice(item) + summaryBabyAdditionalPrice(item);
                      const addGuestPriceStr = item?.booking_items?.length < 2 ? `${t('charge_fee_per_person')} ${numberFormat(item?.booking_items[0]?.additional_guest_price, true)} ${t('currency_unit')}/${t('nights')}` :
                        `${t('charge_fee')} ${item?.booking_items?.map((b) => `${moment(b.date).format('DD/MM/YYYY')} ${t('price_per_person')} ${numberFormat(b.additional_guest_price, true)} ${t('currency_unit')}`).join(', ')}`;
                      const addBabyPriceStr = item?.booking_items?.length < 2 ? `${t('charge_fee_per_person')} ${numberFormat(item?.booking_items[0]?.additional_baby_price, true)} ${t('currency_unit')}/${t('nights')}` :
                        `${t('charge_fee')} ${item?.booking_items?.map((b) => `${moment(b.date).format('DD/MM/YYYY')} ${t('price_per_person')} ${numberFormat(b.additional_baby_price, true)} ${t('currency_unit')}`).join(', ')}`;

                      const allowPets = currentHotel?.addition_pets_type === 'all-rooms' || (currentHotel?.addition_pets_type === 'specific-rooms' && item?.allow_pet);
                      const bookingPet = allowPets && item?.bookingPets && item?.bookingPets.length > 0 ? item.bookingPets[0] : null;
                      return (
                        <Card key={`cart-item-card-${index}`} className="mb-3">
                          <Card.Body>
                            <div className="d-flex justify-content-between align-items-center mb-2" onClick={() => {
                              setCartItemCollapse(item.id, !item.hideCollapse)
                            }} style={{ cursor: 'pointer' }} aria-controls={`cart-item-collapse-${index}`} aria-expanded={!item.hideCollapse}>
                              <h4 style={{ flex: 1 }}><i className={`fas fa-chevron-${!item.hideCollapse ? 'up' : 'down'}`}></i> {transformFieldWithLang(item, 'name', lang)}
                                {isUnavailable && <span className="ms-2 badge bg-danger">{t('room_unavailable')}</span>}
                              </h4>
                              <Button variant="danger" size="sm" onClick={(e) => {
                                e.stopPropagation();
                                if (bookingCartData?.cart_items.length === 1) {
                                  updateBookingCart({});
                                  return;
                                }

                                let cartItems = bookingCartData?.cart_items || [];
                                const newCartItems = cartItems.filter((ci) => ci.id !== item.id);
                                updateBookingCart({
                                  ...bookingCartData,
                                  cart_items: newCartItems,
                                });
                              }}><i className="fal fa-trash-alt"></i></Button>
                              {/* <span className="text-muted ps-3" style={{width: 120}}><i className={`fas fa-chevron-${!item.hideCollapse ? 'up' : 'down'}`}></i> {!item.hideCollapse ? 'ย่อกล่องนี้' : 'ขยายกล่องนี้'}</span> */}
                            </div>
                            <Collapse in={!item.hideCollapse}>
                              <div id={`cart-item-collapse-${index}`}>
                                <p className="mb-1 text-success">{t('room_price')}: {numberFormat(priceTotal, true)} {t('currency_unit')} {dayQty > 1 && <small className="text-muted ms-1">{bookingDatesStr}</small>}</p>
                                {roomDepositPrice > 0 && <div><span className="text-danger">{t('security_deposit')} : {numberFormat(roomDepositPrice, true)} {t('currency_unit')} {roomDepositPayOnCheckin ? `(${t('security_deposit_info_pay_on_stay')})` : `(${t('security_deposit_info_pay_on_booking')})`} </span></div>}
                                {/* >> item form */}
                                <div className="border p-3 mt-2 rounded booking-form-input">
                                  <Row>
                                    <Col xs={6}>
                                      <input
                                        type="number"
                                        name={`guest_qty_${index}`}
                                        placeholder={t('guest_adult_qty')}
                                        min={1}
                                        onKeyPress={inputKeyPressOnlyNumber} value={item.form_guest_qty || ""}
                                        onChange={(e) => {
                                          const guestQty = Number(e.target.value || 0)
                                          if (validateGuestQty(item, guestQty)) {
                                            setCartItemForm(item.id, guestQty, 'form_guest_qty')
                                          } else {
                                            setCartItemForm(item.id, item?.guest_qty, 'form_guest_qty')
                                          }
                                        }}
                                        required
                                      />
                                    </Col>
                                    <Col xs={6}>
                                      <input
                                        type="number"
                                        name={`guest_baby_qty_${index}`}
                                        placeholder={t('guest_baby_qty', { age: currentHotel?.custom_guest_baby_age || 6 })}
                                        min={1}
                                        onKeyPress={inputKeyPressOnlyNumber} value={item.form_guest_baby_qty || ""}
                                        onChange={(e) => {
                                          const guestQty = Number(e.target.value || 0)
                                          if (validateBabyQty(item, guestQty)) {
                                            setCartItemForm(item.id, guestQty, 'form_guest_baby_qty')
                                          } else {
                                            setCartItemForm(item.id, item?.guest_baby_qty, 'form_guest_baby_qty')
                                          }
                                        }}
                                      />
                                    </Col>
                                  </Row>
                                  <Row className="mt-2">
                                    <Col xs={12}>
                                      <textarea
                                        type="text"
                                        name={`booking_note_${index}`}
                                        placeholder={t('special_request')}
                                        value={item.form_booking_note || ""}
                                        onChange={(e) => setCartItemForm(item.id, e.target.value, 'form_booking_note')}
                                      />
                                    </Col>
                                  </Row>
                                </div>
                                {item.additional_guest_enabled && <div className="border p-3 mt-2 rounded booking-form-input">
                                  <strong className="text-warning">{t('guest_additional')}</strong> <span className="text-muted">{t('guest_additional_condition')}</span>
                                  {dayQty < 2 && <ul>
                                    <li>{t('adult')}: {t('guest_additional_limit', { limit: item.additional_guest_qty || 0 })} {addGuestPriceStr}</li>
                                    {item.guest_baby_fixed_enabled && Number(item.additional_baby_qty) > 0 && <li>{t('children')}: {t('guest_additional_limit', { limit: item.additional_baby_qty || 0 })} {addBabyPriceStr}</li>}
                                  </ul>}
                                  {dayQty >= 2 && <div className="mb-2">
                                    <div>{t('adult')}: {t('guest_additional_limit', { limit: item.additional_guest_qty || 0 })}</div>
                                    <ul>
                                      {item.booking_items && item.booking_items.map((b, bi) => {
                                        return (
                                          <li className="ms-2" key={`add-guest-price-day-${bi}`}><small>- {moment(b.date).format('DD/MM/YYYY')} : {numberFormat(b.additional_guest_price || 0, true)} {t('currency_unit')}/{t('person')}</small></li>
                                        )
                                      })}
                                    </ul>
                                    {item.guest_baby_fixed_enabled && Number(item.additional_baby_qty) > 0 && <div>{t('children')}: {t('guest_additional_limit', { limit: item.additional_baby_qty || 0 })}</div>}
                                    {item.guest_baby_fixed_enabled && Number(item.additional_baby_qty) > 0 && <ul>
                                      {item.booking_items && item.booking_items.map((b, bi) => {
                                        return (
                                          <li className="ms-2" key={`add-baby-price-day-${bi}`}><small>- {moment(b.date).format('DD/MM/YYYY')} : {numberFormat(b.additional_baby_price || 0, true)} {t('currency_unit')}/{t('person')}</small></li>
                                        )
                                      })}
                                    </ul>}
                                  </div>}
                                  {roomAdditionalPrice > 0 && <div className="text-primary">{t('guest_additional_total_price')} : {numberFormat(roomAdditionalPrice, true)} {t('currency_unit')}</div>}
                                  <Row className="mt-2">
                                    <Col xs={6}>
                                      <input
                                        type="number"
                                        name={`additional_guest_qty_${index}`}
                                        placeholder={`${t('guest_additional_qty')} (${t('adult')})`}
                                        min={0}
                                        onKeyPress={inputKeyPressOnlyNumber} value={item.form_additional_guest_qty || ""}
                                        onChange={(e) => {
                                          const guestQty = Number(e.target.value || 0)
                                          if (validateAdditionalGuestQty(item, guestQty)) {
                                            setCartItemForm(item.id, guestQty, 'form_additional_guest_qty')
                                          }
                                        }}
                                      />
                                    </Col>
                                    {item.guest_baby_fixed_enabled && Number(item.additional_baby_qty) > 0 && <Col xs={6}>
                                      <input
                                        type="number"
                                        name={`additional_baby_qty_${index}`}
                                        placeholder={`${t('guest_additional_qty')} (${t('children')})`}
                                        min={0}
                                        onKeyPress={inputKeyPressOnlyNumber} value={item.form_additional_baby_qty || ""}
                                        onChange={(e) => {
                                          const guestQty = Number(e.target.value || 0)
                                          if (validateAdditionalBabyQty(item, guestQty)) {
                                            setCartItemForm(item.id, guestQty, 'form_additional_baby_qty')
                                          }
                                        }}
                                      />
                                    </Col>}
                                  </Row>
                                </div>}
                                {/* << item form */}
                                {/* >> additional pets */}
                                {/* ออกแบบ data ไว้เผื่อเพิ่มได้หลายประเภท แต่ ui ให้เพิ่มได้ 1 ประเภทก่อน */}
                                {allowPets && <div className="border p-3 mt-2 rounded">
                                  <strong className="text-warning">{t('pet_additional')}</strong> <small className="text-muted">{t('pet_additional_condition')}</small>
                                  <div className="mt-1 d-flex">
                                    <div className="top-label" style={{ flex: 1 }}>
                                      <Form.Label className="mb-0" htmlFor={`additional_pet`}>{t('pet_type')}</Form.Label>
                                      <Select
                                        options={petOptions}
                                        onChange={(option) => {
                                          if (!option) {
                                            setAdditionalPetsData(item.id, []);
                                            return;
                                          }

                                          let petPricePerQtyUnit = _.sumBy(option._prices || [], (dp) => { return Number(dp.price || 0) }) || 0;
                                          const _qtyLimit = option.qty_limit || 0;
                                          let _qty = bookingPet?.qty || 1;
                                          if (_qtyLimit > 0 && _qty > _qtyLimit) {
                                            _qty = _qtyLimit;
                                            toast.warn(t('pet_qty_limit_exceed', { limit: _qtyLimit }));
                                          }

                                          setAdditionalPetsData(item.id, [{
                                            pet_id: option.value,
                                            unit_price: petPricePerQtyUnit,
                                            total_price: petPricePerQtyUnit * _qty,
                                            qty: _qty,
                                            qty_limit: _qtyLimit,
                                            _prices: option._prices || [],
                                          }]);
                                        }}
                                        className={'flex-fill'}
                                        placeholder={t('select_pet_type')}
                                        value={optionValue(bookingPet?.pet_id, petOptions)}
                                        classNamePrefix="react-select"
                                        id={`additional_pet`}
                                        isClearable
                                      />
                                    </div>
                                    <div className="top-label ms-2" style={{ width: 70 }}>
                                      <Form.Label className="mb-0" htmlFor={`additional_pet_qty`}>{t('qty')}</Form.Label>
                                      <Form.Control id={`additional_pet_qty`} type="number" min="1" onKeyPress={inputKeyPressOnlyNumber} value={bookingPet?.qty || ""} onChange={(e) => {
                                        const _qty = Number(e.target.value || 0)
                                        const _selectedPetOption = petOptions.find(p => p.value === bookingPet?.pet_id);
                                        const _qtyLimit = _selectedPetOption?.qty_limit || bookingPet?.qty_limit || 0;
                                        if (_qtyLimit > 0 && _qty > _qtyLimit) {
                                          toast.warn(t('pet_qty_limit_exceed', { limit: _qtyLimit }));
                                          return;
                                        }
                                        setAdditionalPetsData(item.id, [{
                                          ...bookingPet,
                                          qty: _qty,
                                          total_price: Number(_qty) * Number(bookingPet.unit_price),
                                        }]);
                                      }} />
                                    </div>
                                  </div>
                                  {bookingPet && <div className="mt-2">
                                    <ul>
                                      {bookingPet._prices && bookingPet._prices.length > 0 && bookingPet._prices.map((bp, bpi) => {
                                        return (
                                          <li className="ms-2" key={`pet-price-day-${bpi}`}><small>- {moment(bp.date).format('DD/MM/YYYY')} : {numberFormat(bp.price || 0, true)} {t('currency_unit')}/{t('pet_unit')}</small></li>
                                        )
                                      })}
                                    </ul>
                                    <div className="text-primary">{t('pet_additional_total_price')} : {numberFormat(bookingPet.total_price, true)} {t('currency_unit')}</div>
                                  </div>}
                                </div>}
                                {/* < additional pets */}
                                {/* >> free addons */}
                                {freeAddonOptions.length > 0 && <div className="border p-3 mt-2 rounded">
                                  <strong className="text-success">{t('free_addons')} <small className="text-danger">* {t('required')}</small></strong>
                                  {!item.bookingFreeAddon?.free_addon_id && <small className="ms-2 text-danger">* {t('please_select_free_addon')}</small>}
                                  <div className="mt-2">
                                    <Select
                                      options={freeAddonOptions}
                                      onChange={(option) => {
                                        if (!option) {
                                          setCartFreeAddonData(item.id, null);
                                          return;
                                        }
                                        setCartFreeAddonData(item.id, { free_addon_id: option.value, name: option.label });
                                      }}
                                      placeholder={t('select_free_addon')}
                                      value={item.bookingFreeAddon?.free_addon_id ? { label: item.bookingFreeAddon.name, value: item.bookingFreeAddon.free_addon_id } : null}
                                      classNamePrefix="react-select"
                                      isClearable
                                    />
                                  </div>
                                </div>}
                                {/* << free addons */}
                                {/* >> food menu (อาหารเช้า 1 ห้อง 1 เมนู) */}
                                {foodMenuOptions.length > 0 && <div className="border p-3 mt-2 rounded">
                                  <strong className="text-success">{t('food_menu')} <small className="text-danger">* {t('required')}</small></strong>
                                  {!foodMenuOptions.some((o) => o.value === item.bookingFoodMenu?.food_menu_id) && <small className="ms-2 text-danger">* {t('please_select_food_menu')}</small>}
                                  <div className="mt-2">
                                    <Select
                                      options={foodMenuOptions}
                                      onChange={(option) => setCartFoodMenuData(item.id, option ? { food_menu_id: option.value } : null)}
                                      placeholder={t('select_food_menu')}
                                      value={foodMenuOptions.find((o) => o.value === item.bookingFoodMenu?.food_menu_id) || null}
                                      classNamePrefix="react-select"
                                      isSearchable={false}
                                    />
                                  </div>
                                </div>}
                                {/* << food menu */}
                                {/* >> addons */}
                                {addonOptions && addonOptions.length > 0 && <div className="mt-2">
                                  <strong className={`text-${!addonCustomTitle ? 'danger' : 'warning'}`}>{addonCustomTitle || `${t('addons')} (${t('optional')})`}</strong>
                                  {!!addonCustomWarning && <small className="ms-2 text-danger">*{addonCustomWarning}</small>}
                                  <div className="mb-3">
                                    {item.bookingAddons && item.bookingAddons.length > 0 && item.bookingAddons.map((ad, adi) => {
                                      return (
                                        <div className="mt-3" key={`addons-list-${adi}`}>
                                          <div className="d-block d-md-none mb-3">
                                            <div className="top-label" style={{ flex: 1 }}>
                                              <Form.Label className="mb-0" htmlFor={`addons_name_${adi}`}>{t('addons')} {adi + 1}</Form.Label>
                                              <Select
                                                options={addonOptions}
                                                onChange={(option) => {
                                                  let ads = [...item.bookingAddons || []]
                                                  ad.addons_id = option.value
                                                  ad.unit_price = option._price
                                                  ad.total_price = ((Number(option._price) * Number(ad.qty)) || 0)
                                                  ads[adi] = ad
                                                  setCartAddonsData(item.id, ads);
                                                }}
                                                className={'flex-fill'}
                                                placeholder={t('select_addons')}
                                                value={optionValue(ad.addons_id, addonOptions)}
                                                classNamePrefix="react-select"
                                                id={`addons_name_${adi}`}
                                              />
                                            </div>
                                            <div className="d-flex align-items-end">
                                              <div className="top-label" style={{ width: 100 }}>
                                                <Form.Label className="mb-0" htmlFor={`addons_qty_${adi}`}>{t('qty')}</Form.Label>
                                                <Form.Control id={`addons_qty_${adi}`} type="number" min="1" onKeyPress={inputKeyPressOnlyNumber} value={ad.qty || ""} onChange={(e) => {
                                                  let ads = [...item.bookingAddons || []]
                                                  ad.qty = e.target.value
                                                  ad.total_price = Number(e.target.value) * Number(ad.unit_price)
                                                  ads[adi] = ad
                                                  setCartAddonsData(item.id, ads);
                                                }} />
                                              </div>
                                              <div className="top-label ms-2" style={{ flex: 1 }}>
                                                <Form.Label className="mb-0" htmlFor={`addons_price_${adi}`}>{t('price')}</Form.Label>
                                                <Form.Control id={`addons_price_${adi}`} type="text" value={numberFormat(ad.total_price) || ""} disabled />
                                              </div>
                                              <Button variant="outline-danger" disabled={submitting} className="btn-icon btn-icon-only ms-2 h-50" onClick={() => {
                                                let ads = [...item.bookingAddons || []]
                                                ads.splice(adi, 1)
                                                setCartAddonsData(item.id, ads);
                                              }}>
                                                <i className="fa fa-trash" />
                                              </Button>
                                            </div>
                                            <hr style={{ color: 'darkgray' }} />
                                          </div>
                                          <div className="d-none d-md-flex mb-3">
                                            <div className="top-label" style={{ flex: 1 }}>
                                              <Form.Label className="mb-0" htmlFor={`addons_name_${adi}`}>{t('addons')} {adi + 1}</Form.Label>
                                              <Select
                                                options={addonOptions}
                                                onChange={(option) => {
                                                  let ads = [...item.bookingAddons || []]
                                                  ad.addons_id = option.value
                                                  ad.unit_price = option._price
                                                  ad.total_price = ((Number(option._price) * Number(ad.qty)) || 0)
                                                  ads[adi] = ad
                                                  setCartAddonsData(item.id, ads);
                                                }}
                                                className={'flex-fill'}
                                                placeholder={t('select_addons')}
                                                value={optionValue(ad.addons_id, addonOptions)}
                                                classNamePrefix="react-select"
                                                id={`addons_name_${adi}`}
                                              />
                                            </div>
                                            <div className="top-label ms-2" style={{ width: 70 }}>
                                              <Form.Label className="mb-0" htmlFor={`addons_qty_${adi}`}>{t('qty')}</Form.Label>
                                              <Form.Control id={`addons_qty_${adi}`} type="number" min="1" onKeyPress={inputKeyPressOnlyNumber} value={ad.qty || ""} onChange={(e) => {
                                                let ads = [...item.bookingAddons || []]
                                                ad.qty = e.target.value
                                                ad.total_price = Number(e.target.value) * Number(ad.unit_price)
                                                ads[adi] = ad
                                                setCartAddonsData(item.id, ads);
                                              }} />
                                            </div>
                                            <div className="top-label ms-2" style={{ width: 100 }}>
                                              <Form.Label className="mb-0" htmlFor={`addons_price_${adi}`}>{t('price')}</Form.Label>
                                              <Form.Control id={`addons_price_${adi}`} type="text" value={numberFormat(ad.total_price) || ""} disabled />
                                            </div>
                                            <Button variant="outline-danger" disabled={submitting} className="btn-icon btn-icon-only ms-2" onClick={() => {
                                              let ads = [...item.bookingAddons || []]
                                              ads.splice(adi, 1)
                                              setCartAddonsData(item.id, ads);
                                            }}>
                                              <i className="fa fa-trash" />
                                            </Button>
                                          </div>
                                        </div>
                                      )
                                    })}
                                  </div>
                                  {item?.bookingAddons && item?.bookingAddons.length > 0 && <div className="text-primary">{t('addons_summary_price', { price: numberFormat((addonsPrice || 0), true) })}
                                    {(form.payment_type === 'deposit' && !!currentHotel?.deposit_addon_enabled) && addonPriceForPay > 0 && <small className="text-muted ms-2">({t('payment_method_deposit')} {numberFormat(addonPriceForPay, true)} {t('currency_unit')})</small>}
                                  </div>}
                                  <Button variant="light" disabled={submitting} className="btn-icon btn-sm" onClick={() => {
                                    let ads = [...item.bookingAddons || []]
                                    ads.push({ name: '', qty: 1, total_price: 0, unit_price: 0 })
                                    setCartAddonsData(item.id, ads);
                                  }}>
                                    <i className="fa fa-plus me-2" />
                                    {t('add_addons')}
                                  </Button>
                                </div>}
                                {/* << addons */}

                                {/* >> discount */}
                                {(!form.payment_type || form.payment_type === 'full' || (!!currentHotel && currentHotel?.allow_usediscount_with_patialpayment)) && <div className="mt-3 border p-2 rounded booking-form-input">
                                  <strong className="text-warning">{t('discount_coupon')} ({t('optional')})</strong>

                                  {!discount && <Row className="mb-1">
                                    <Col xs={8}>
                                      <input
                                        type="text"
                                        name={`coupon_code_${index}`}
                                        placeholder={t('specify_discount_code')}
                                        value={couponCode || ""}
                                        onChange={(e) => setCartItemForm(item.id, e.target.value, 'coupon_code')}
                                      />
                                    </Col>
                                    <Col xs={4}>
                                      <Button disabled={!couponCode?.length} className="btn btn-sm btn-success" onClick={() => applyCouponCode(item, couponCode)}>
                                        {t('use_discount')}
                                      </Button>
                                      <p className="mb-0 text-danger" onClick={() => setShowSuggestionModal(true)}><i className="fas fa-info-circle pe-1 text-danger" style={{ fontSize: 14 }}></i><small>{t('discount_suggestion')}</small></p>
                                    </Col>
                                  </Row>}
                                  {!!discount && <Row className="mb-1">
                                    <Col xs={8}>
                                      <strong className="text-success"> {t('discount_price', { price: numberFormat(discountPrice) })}</strong>
                                    </Col>
                                    <Col xs={4}>
                                      <Button className="btn btn-sm btn-danger" onClick={() => {
                                        clearPendingPromo(couponCode)
                                        setCartItemForm(item.id, '', 'coupon_code')
                                        setCartItemForm(item.id, null, 'discount')
                                      }}>
                                        {t('cancel_discount')}
                                      </Button>
                                    </Col>
                                  </Row>}
                                </div>}
                                {/* << discount */}
                              </div>
                            </Collapse>
                          </Card.Body>
                        </Card>
                      )
                    })}
                    <Link className="py-2" to={`/${currentHotel?.code}`}>
                      <span className="text-success border border-success rounded p-1"><i className="fal fa-cart-plus" style={{ fontSize: 18 }}></i> {t('select_more_rooms')}</span>
                    </Link>
                  </div>

                </div>}
              </Col>
              {bookingCartData?.cart_items?.length > 0 && <Col xs={12} md={6}>
                <Card className="mb-3">
                  <Card.Body>
                    <h4 className="mb-3">{t('booking_summary')}</h4>
                    <div style={{ overflowX: 'auto' }}>
                      <table className="table table-bordered">
                        <thead>
                          <tr>
                            <td className="text-center">{t('room')}</td>
                            <td className="text-center">{t('rental_price')}</td>
                            <td className="text-center">{t('additional')}</td>
                            <td className="text-center">{t('discount')}</td>
                            <td className="text-center">{t('net_total')}</td>
                            {hotelDepositEnabled && <td className="text-center">{t('deposit')}</td>}
                            <td className="text-center">{t('security_deposit_short')}</td>
                          </tr>
                        </thead>
                        <tbody>
                          {bookingCartData?.cart_items?.map((item, index) => {
                            const priceTotal = _.sumBy(item?.booking_items, 'price');
                            const discountPrice = discountCalculated(item.id);
                            const addonsPrices = _.sumBy(item?.bookingAddons || [], (x) => { return Number(x.total_price || 0) }) || 0;
                            const additionalGuestPrice = summaryGuestAdditionalPrice(item) + summaryBabyAdditionalPrice(item);
                            const additionalPetPrice = _.sumBy(item?.bookingPets || [], (x) => { return Number(x.total_price || 0) }) || 0;
                            const sumAddons = Number(addonsPrices || 0) + Number(additionalGuestPrice || 0) + Number(additionalPetPrice || 0);

                            // >> Deposit Calculation
                            const depositConfigType = item?.deposit_enabled ? 'room' : (currentHotel?.deposit_enabled ? 'hotel' : null)

                            const depositEnabled = !!depositConfigType
                            const depositeType = depositConfigType === 'room' ? item?.deposit_type : currentHotel?.deposit_type
                            const depositValue = depositConfigType === 'room' ? (item?.deposit_value || 0) : (currentHotel?.deposit_value || 0)

                            const depositPrice = depositeType === 'static' ? depositValue : ((Number(priceTotal || 0) - (!!currentHotel?.allow_usediscount_with_patialpayment ? discountPrice : 0)) * (depositValue / 100)) || 0
                            const addonPriceForPay = ((!currentHotel?.deposit_addon_enabled) ? sumAddons : (depositeType === 'static' ? 0 : (Number(sumAddons) * (depositValue / 100)))) || 0

                            const roomDepositEnabled = item?.room_deposit_enabled || currentHotel?.room_deposit_enabled || false
                            const roomDepositPrice = roomDepositEnabled ? (item?.room_deposit_price || currentHotel?.room_deposit_price || 0) : 0
                            const roomDepositPayOnCheckin = currentHotel?.room_deposit_payoncheckin || false
                            const roomDepositPayAmount = roomDepositPayOnCheckin ? 0 : roomDepositPrice
                            // << Deposit Calculation

                            const totalAmount = priceTotal - discountPrice + sumAddons
                            const payAmount = (form.payment_type === 'full' ? (Number(priceTotal || 0) - discountPrice + sumAddons) : (form.payment_type === 'payonstay' ? (sumAddons || 0) : (Number(depositPrice || 0) + Number(addonPriceForPay)))) + Number(roomDepositPayAmount || 0)

                            const isUnavailable = roomUnavailableIds.includes(item.id);

                            return (
                              <tr key={index}>
                                <td>
                                  <div>{transformFieldWithLang(item, 'name', lang)}
                                    {isUnavailable && <Button variant="danger" size="sm" className="ms-2" onClick={(e) => {
                                      if (bookingCartData?.cart_items.length === 1) {
                                        updateBookingCart({});
                                        return;
                                      }

                                      let cartItems = bookingCartData?.cart_items || [];
                                      const newCartItems = cartItems.filter((ci) => ci.id !== item.id);
                                      updateBookingCart({
                                        ...bookingCartData,
                                        cart_items: newCartItems,
                                      });
                                    }}><i className="fal fa-trash-alt"></i></Button>}
                                  </div>
                                  {isUnavailable && <small className="text-danger">{t('room_unavailable_in_cart_info')}</small>}
                                </td>
                                <td className={`${isUnavailable ? 'text-danger' : ''} text-end`}>{numberFormat(priceTotal, true)}</td>
                                <td className={`${isUnavailable ? 'text-danger' : ''} text-end`}>{numberFormat(sumAddons, true, '-')}</td>
                                <td className={`${isUnavailable ? 'text-danger' : ''} text-end`}>{numberFormat(discountPrice, true, '-')}</td>
                                <td className={`${isUnavailable ? 'text-danger' : ''} text-end`}>{numberFormat(totalAmount, true)}</td>
                                {hotelDepositEnabled && <td className="text-end">{numberFormat((depositPrice + addonPriceForPay), true, '-')}</td>}
                                <td className={`${isUnavailable ? 'text-danger' : ''} text-end`}>{numberFormat(roomDepositPrice, true, '-')}</td>
                              </tr>
                            )
                          })}
                          <tr>
                            <td colSpan={4} className="text-end"><strong>{t('total_amount')}</strong></td>
                            <td className={`text-end ${checkCartItemsUnavailable() ? 'text-danger' : ''}`}><strong>{checkCartItemsUnavailable() ? '* ' : ''}{numberFormat(_.sumBy(bookingCartData?.cart_items || [], (item) => {
                              const priceTotal = _.sumBy(item?.booking_items, 'price');
                              const discountPrice = discountCalculated(item.id);
                              const addonsPrices = _.sumBy(item?.bookingAddons || [], (x) => { return Number(x.total_price || 0) }) || 0;
                              const additionalGuestPrice = summaryGuestAdditionalPrice(item) + summaryBabyAdditionalPrice(item);
                              const additionalPetPrice = _.sumBy(item?.bookingPets || [], (x) => { return Number(x.total_price || 0) }) || 0;
                              const sumAddons = Number(addonsPrices || 0) + Number(additionalGuestPrice || 0) + Number(additionalPetPrice || 0);

                              return priceTotal - discountPrice + sumAddons
                            }), true)}</strong></td>
                            {hotelDepositEnabled && <td className="text-end"><strong>{numberFormat(getDepositAmount(), true, '-')}</strong></td>}
                            <td className="text-end"><strong>{numberFormat(_.sumBy(bookingCartData?.cart_items || [], (item) => {
                              const roomDepositEnabled = item?.room_deposit_enabled || currentHotel?.room_deposit_enabled || false;
                              const roomDepositPrice = Number(roomDepositEnabled ? (item?.room_deposit_price || currentHotel?.room_deposit_price || 0) : 0)
                              return roomDepositPrice
                            }), true, '-')}</strong></td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                    <div>
                      {payChoices.length > 1 && <PaymentTypeChoices
                        ref={payChoicesRef}
                        choices={payChoices.map((type) => ({ type, breakdown: cartBreakdown(type) }))}
                        value={form.payment_type}
                        invalid={payTypeInvalid && !form.payment_type}
                        onChange={(type) => { setPayTypeInvalid(false); setForm({ ...form, payment_type: type }) }}
                        note={!!currentHotel && !currentHotel?.allow_usediscount_with_patialpayment ? t('discount_partialpayment_not_allowed') : null}
                      />}
                    </div>
                    {!!form.payment_type && <div className="mt-3">
                      <h5>{t('pay_total_amount')} : <span className="text-danger">{numberFormat(getPayAmount(), true)} {t('currency_unit')}</span> </h5>
                    </div>}
                  </Card.Body>
                </Card>
                <Card>
                  <Card.Body>
                    <div className="booking-form-input">
                      <Row className="mt-2">
                        <Col xs={12}>
                          {!form.contact_name && <small className="text-danger">* "{t('contact_name')}" {t('form_required')}</small>}
                          <input
                            type="text"
                            name="booking_contact_name"
                            placeholder={t('please_specify_contact_name')}
                            value={form.contact_name || ""}
                            onChange={(e) => setForm({ ...form, contact_name: e.target.value })}
                            required
                          />
                        </Col>
                      </Row>
                      {!!currentHotel && currentHotel.request_customer_tax_info_enabled && <Row className="mt-2">
                        <Col xs={12}>
                          {!form.customer_tax_id && <small className="text-danger">* "{t('customer_tax_id')}" {t('form_required')}</small>}
                          <input
                            type="text"
                            name="booking_customer_tax_id"
                            placeholder="{t('please_specify_customer_tax_id')}"
                            onKeyPress={inputKeyPressOnlyNumber}
                            maxLength={13}
                            onBlur={(e) => checkInputIsTaxID(e.target.value)}
                            value={form.customer_tax_id || ""}
                            onChange={(e) => setForm({ ...form, customer_tax_id: e.target.value })}
                            required
                          />
                        </Col>
                      </Row>}
                      {!!currentHotel && currentHotel.request_customer_tax_info_enabled && <Row className="mt-2">
                        <Col xs={12}>
                          {!form.customer_address && <small className="text-warning">* {t('tax_invoice_info')}</small>}
                          <textarea
                            type="text"
                            name="customer_address"
                            placeholder={t('tax_invoice_address')}
                            value={form.customer_address || ""}
                            onChange={(e) => setForm({ ...form, customer_address: e.target.value })}
                          />
                        </Col>
                      </Row>}
                      <Row className="mt-2">
                        {!form.contact_tel_1 && <small className="text-danger">* "{t('contact_tel_1')}" {t('form_required')}</small>}
                        <Col xs={6}>
                          <input
                            type="text"
                            name="booking_tel_1"
                            placeholder={t('contact_tel_1')}
                            onKeyPress={inputKeyPressOnlyNumber} value={form.contact_tel_1 || ""}
                            onChange={(e) => {
                              if (e.nativeEvent?.inputType === "insertFromPaste" && e.target.value) {
                                const t = e.target.value.replace(/\D/g, '')
                                setForm({ ...form, contact_tel_1: t })
                              } else {
                                setForm({ ...form, contact_tel_1: e.target.value })
                              }
                            }}
                            required
                          />
                        </Col>
                        <Col xs={6}>
                          <input
                            type="text"
                            name="booking_tel_2"
                            placeholder={t('contact_tel_2')}
                            onKeyPress={inputKeyPressOnlyNumber} value={form.contact_tel_2 || ""}
                            onChange={(e) => setForm({ ...form, contact_tel_2: e.target.value })}
                          />
                        </Col>
                      </Row>
                      <Row className="mt-2">
                        <Col xs={6}>
                          <input
                            type="text"
                            name="contact_email"
                            placeholder={t('email')}
                            value={form.contact_email || ""}
                            onChange={(e) => setForm({ ...form, contact_email: e.target.value })}
                          />
                        </Col>
                        <Col xs={6}>
                          <input
                            type="text"
                            name="contact_line_id"
                            placeholder={t('line_id')}
                            value={form.contact_line_id || ""}
                            onChange={(e) => setForm({ ...form, contact_line_id: e.target.value })}
                          />
                        </Col>
                      </Row>
                      <Row className="mt-2">
                        <Col xs={6}>
                          <input
                            type="text"
                            name="customer_car_model"
                            placeholder={t('car_model')}
                            value={form.customer_car_model || ""}
                            onChange={(e) => setForm({ ...form, customer_car_model: e.target.value })}
                          />
                        </Col>
                        <Col xs={6}>
                          <input
                            type="text"
                            name="customer_car_license_plate"
                            placeholder={t('car_license_plate')}
                            value={form.customer_car_license_plate || ""}
                            onChange={(e) => setForm({ ...form, customer_car_license_plate: e.target.value })}
                          />
                        </Col>
                      </Row>
                    </div>
                  </Card.Body>
                </Card>
                {!isGatewayFlow && !!paymentInfo && getPayAmount() > 0 && <Card className="mt-3">
                  <Card.Body>
                    <p className="mb-2 text-black">{t('payment_methods')}</p>
                    <div className="booking-payment-info">
                      <div dangerouslySetInnerHTML={{ __html: paymentInfo }} />
                    </div>
                  </Card.Body>
                </Card>}
                {isGatewayFlow && getPayAmount() > 0 && <Card className="mt-3">
                  <Card.Body>
                    <p className="mb-2 text-black">{t('payment_methods')}</p>
                    <div className="text-muted">{t('omise_pay_after_confirm')}</div>
                  </Card.Body>
                </Card>}
                {!isGatewayFlow && !!acceptPdpa && getPayAmount() > 0 && <Card className="mt-3" ref={slipSectionRef}>
                  <Card.Body>
                    {/* ย้ำยอดที่ต้องโอนตรงจุดแนบสลิป -- ลูกค้าเคยโอนแค่ยอดมัดจำทั้งที่เลือกจ่ายเต็ม */}
                    <div className="pay-transfer-amount">
                      <div>
                        <div className="pay-transfer-amount__label">{t('transfer_amount_title')} · {t(form.payment_type === 'deposit' ? 'payment_method_deposit' : (form.payment_type === 'payonstay' ? 'payment_method_pay_on_stay' : 'payment_method_full'))}</div>
                        <div className="pay-transfer-amount__value">฿{numberFormat(getPayAmount(), true)}</div>
                      </div>
                      {payChoices.length > 1 && <button type="button" className="pay-transfer-amount__change" onClick={() => payChoicesRef.current && payChoicesRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' })}>{t('change')}</button>}
                    </div>
                    <div className="d-flex mb-2 align-items-end">
                      <div style={{ flex: 1 }}>
                        <label htmlFor={`form_file_slip`} className="form-label">{t('attach_slip_payment')}</label>
                        <input accept="image/*" className="form-control" id={`form_file_slip`} type="file" style={{ fontSize: 16 }} onChange={(e) => {
                          if (e?.target?.files && e?.target?.files[0]) {
                            setForm({ ...form, slip_file: e.target.files[0] })
                          }
                        }} />
                      </div>
                      {!!form.slip_file && <div className="ms-3">
                        <img src={URL.createObjectURL(form.slip_file)} className="rounded" style={{ width: 80, height: 80, objectFit: 'cover', cursor: 'pointer' }} onClick={() => setIsLightboxOpen(true)} />
                      </div>}
                    </div>
                  </Card.Body>
                </Card>}
                <div className="text-center mb-5" ref={submitButtonRef}>
                  <Button
                    className="rounded btn-lg mt-4"
                    variant={acceptPdpa ? 'success' : 'primary'}
                    disabled={submitting || bookingCartData?.cart_items?.length === 0 || !form.contact_name || !form.contact_tel_1}
                    onClick={() => {
                      if (!acceptPdpa) {
                        ReactGA.event({
                          category: "BookingProcess",
                          action: `${currentHotel?.code}__pdpa_click`,
                          label: `Click for PDPA Modal - ${currentHotel?.name_th}`,
                        });
                      } else {
                        ReactGA.event({
                          category: "Booking Submit Button",
                          action: `${currentHotel?.code}_submit_booking`,
                          label: `Click for Submit Booking - ${currentHotel?.name_th}`,
                        });
                      }

                      if (!requirePaymentType()) return

                      if (!acceptPdpa) {
                        // ที่พัก gateway: ตรวจข้อมูลครบก่อนเปิดเงื่อนไข -- ยอมรับแล้วไปหน้าจ่ายเงินทันที
                        if (isGatewayFlow && getPayAmount() > 0 && !handleSubmitBooking(false, { dryRun: true })) return
                        setShowPolicyModal(true)
                        return
                      }
                      handleSubmitBooking()
                    }}
                  >
                    {submitting
                      ? <><span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />{t('booking_confirmation')}</>
                      : (isGatewayFlow && getPayAmount() > 0)
                        ? `${t('book_and_pay')} ฿${numberFormat(getPayAmount(), true)}`
                        : (acceptPdpa ? t('booking_confirmation') : t('ิbooking_process'))
                    }
                  </Button>
                </div>
              </Col>}
            </Row>
          </div>
        </div >
        <OnceFooter />
        <Modal className="fade modal-close-out" show={showSuggestionModal} onHide={() => setShowSuggestionModal(false)}>
          <Modal.Header closeButton />
          <Modal.Body className="d-flex flex-column p-0">
            <img src={"/assets/img/booking/oncecoupon_suggestion.jpg"} alt="img" />
          </Modal.Body>
        </Modal>
        {isLightboxOpen && !!form.slip_file && (
          <Lightbox
            medium={URL.createObjectURL(form.slip_file)}
            large={URL.createObjectURL(form.slip_file)}
            alt={'Payment Slip'}
            onClose={() => {
              setIsLightboxOpen(false)
            }}
            hideDownload
            hideZoom
          />
        )}
        <Modal show={!!addonQtyWarning} onHide={() => setAddonQtyWarning(null)} centered>
          <Modal.Header closeButton>
            <Modal.Title>{t('addon_qty_warning_title')}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p>{t('addon_qty_warning_body', { nights: dayQty })}</p>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="outline-secondary" onClick={() => setAddonQtyWarning(null)}>
              {t('addon_qty_warning_adjust')}
            </Button>
            <Button variant="primary" onClick={() => {
              addonQtyWarning?.onConfirm?.();
              setAddonQtyWarning(null);
            }}>
              {t('addon_qty_warning_confirm_1_night')}
            </Button>
          </Modal.Footer>
        </Modal>

        <PolicyConfirmModal
          show={showPolicyModal}
          onHide={() => setShowPolicyModal(null)}
          hotel_id={currentHotel?.id}
          confirmLabel={isGatewayFlow && getPayAmount() > 0 ? t('accept_and_pay') : null}
          onAcceptTerm={() => {
            ReactGA.event({
              category: "BookingProcess",
              action: `${currentHotel?.code}_cf_booking_click`,
              label: `Click for Confirm Booking - ${currentHotel?.name_th} `,
            });

            setAcceptPdpa(true)
            setShowPolicyModal(false)

            // ที่พัก gateway: ยอมรับเงื่อนไขแล้วไปหน้าจ่ายเงินเลย -- เดิมต้องกด "ยืนยันการจอง" อีกรอบ
            // และหน้าไม่เลื่อนไปไหน (โค้ดข้างล่างเลื่อนไปหาช่องแนบสลิป ซึ่งไม่มีใน flow นี้)
            // ลูกค้าหลายคนหาปุ่มไปต่อไม่เจอ
            if (isGatewayFlow && getPayAmount() > 0) {
              setTimeout(() => handleSubmitBooking(), 0)
              return
            }

            setTimeout(() => {
              const el = getPayAmount() > 0 ? slipSectionRef.current : submitButtonRef.current;
              if (!el) return;
              const top = el.getBoundingClientRect().top + window.pageYOffset - 16;
              window.scrollTo({ top, behavior: 'smooth' });
            }, 450)
          }}
        />

      {/* กดจองทั้งที่ยังมีรายการค้าง -- ที่นี่ปุ่มยกเลิกคืนข้อมูลกลับเข้าตะกร้าให้ด้วย
          (ต่างจากหน้าอื่นที่ยกเลิกแล้วไปเลือกห้องใหม่เอง) */}
      <BookingBlockedModal
        show={showBlockedModal}
        onHide={() => setShowBlockedModal(false)}
        pending={pendingPayment}
        releasing={releasing}
        onResume={() => navigate(`/once/payment/${pendingPayment.type}/${pendingPayment.id}`, {
          state: { amount: pendingPayment.amount, booking_no: pendingPayment.booking_no, hotel_id: currentHotel?.id },
        })}
        onRelease={() => { setShowBlockedModal(false); cancelPendingAndRestore(); }}
      />

      {/* สรุปสิ่งที่จองไว้ -- ใช้คอมโพเนนต์เดียวกับแถบบนหน้าอื่น จะได้ไม่มีสอง UI */}
      <PendingBookingModal
        show={showPendingDetail}
        onHide={() => setShowPendingDetail(false)}
        pending={pendingPayment}
        onResume={() => navigate(`/once/payment/${pendingPayment.type}/${pendingPayment.id}`, {
          state: { amount: pendingPayment.amount, booking_no: pendingPayment.booking_no, hotel_id: currentHotel?.id },
        })}
      />
      </div >
    </>
  );
}
